import * as Types from './types';
import { getUserId, removeUserId, setUserId } from '../../Utils/localStorage';
import * as Moment from '../../Utils/Moment';
import {
  isTransferSlow,
  isTransferTerminal,
  rememberTransferStart,
  resolveTransferStart,
} from '../../Utils/transferStatus';
import {
  loadSupportChat,
  dropSupportChatOnMismatch,
} from '../../Utils/supportChat';

const initialState = {
  error: null,
  isLoading: true,
  giftcode: null,
  step: 1,
  game: null,
  eod: null,
  user: {
    payload: null,
    isLoading: false,
    error: null,
  },
  location: null,
  userAgreed: {
    payload: false,
    isLoading: false,
  },
  country: {
    payload: null,
    isLoading: false,
    error: null,
  },
  bot: null,
  friendRequest: {
    status: null,
    updatedAt: null,
  },
  transferStatus: null,
  phase: null,
  bonusCode: null,
  transferStartedAt: null,
  pollingStartedAt: null,
};

const GIFT_POLL_INTERVAL_MS = 15 * 1000;
// After 10 minutes without a terminal state the status is polled once a minute
const GIFT_POLL_SLOW_INTERVAL_MS = 60 * 1000;

let giftTimer = null;
// Poll run number: a GET that resolves after stop/restart is ignored
let giftPollRun = 0;

// Код ошибки 107 бэкенда (ActivationService.activate): гифт-код принадлежит другому сайту
export const DOMAIN_MISMATCH = 107;

// Код последнего запроса firstLoading. Сравнивать с getState().main.giftcode нельзя:
// он заполняется только успешным ответом (при ошибке остаётся null/прежним),
// а поздний ответ старого кода сам перезаписывает его своим payload
let lastFirstLoadingCode = null;

export function buildPayload(data, giftcode) {
  const result = {
    ...initialState,
    user: { ...initialState.user },
    userAgreed: { ...initialState.userAgreed },
    country: { ...initialState.country },
    friendRequest: { ...initialState.friendRequest },
    game: data.game,
    giftcode,
  };

  if (data.location) result.location = data.location;
  if (data.eod) result.eod = data.eod;
  if (data.userAgreed) result.userAgreed.payload = data.userAgreed;
  if (data.country) result.country.payload = data.country;

  // Transfer fields come without bot/friendRequest in the Quick Invite → G-Engine flow
  if (data.transferStatus) result.transferStatus = data.transferStatus;
  if (data.transferStartedAt) result.transferStartedAt = data.transferStartedAt;
  if (data.phase) result.phase = data.phase;
  if (data.bonusCode) result.bonusCode = data.bonusCode;

  if (data.user) {
    result.user.payload = data.user;
    result.step = 2;
    if (data.country && data.userAgreed) {
      result.step = 3;
      if (data.bot) {
        result.bot = data.bot;
        if (data.friendRequest) {
          result.friendRequest = data.friendRequest;
          if (data.friendRequest.status === 'DONE') result.step = 4;
        }
      }
    }
  }

  // The status step shows the backend error right away, not after the first GET
  if (result.step >= 3 && data.error) result.error = data.error;
  return result;
}

export const firstLoading = (giftcode, market) => (
  dispatch,
  getState,
  { main }
) => {
  // A route change A → B keeps GetGame mounted: the old poller must die here
  dispatch(stopGiftPolling());
  // Получаем id юзера из ls
  const userId = null;
  const data = (userId && { user: { id: userId } }) || null;
  lastFirstLoadingCode = giftcode;
  // Ответ чужого кода (пользователь сменил код, пока запрос шёл) чат не трогает
  const isCurrent = () => lastFirstLoadingCode === giftcode;
  // console.log('userId', userId);
  // console.log('initData', data);

  main
    .fetchMain(giftcode, market, data)
    .then((data) => {
      const payload = buildPayload(data, giftcode);
      // console.log('payloadData', data);
      // console.log('PAYLOAD', payload);
      // if (payload.user && payload.user.payload && payload.user.payload.id) {
      //   setUserId(payload.user.payload.id);
      // }
      dispatch({
        type: Types.SET_MAIN_INFO,
        payload,
      });
      if (isCurrent()) loadSupportChat();
    })
    .catch((error) => {
      // Чат подключаем до dispatch: на пустой отказ error.error ниже бросит
      if (isCurrent()) {
        if (error && error.error && error.error.code === DOMAIN_MISMATCH) {
          dropSupportChatOnMismatch();
        } else {
          loadSupportChat();
        }
      }
      dispatch({
        type: Types.SET_ERROR,
        payload: error.error,
      });
    });
};

export const activateAccount = (url) => (dispatch, getState, { main }) => {
  const { giftcode } = getState().main;

  dispatch({
    type: Types.ACCOUNT_START,
  });

  main
    .sendUrl(giftcode, url)
    .then(({ user }) => {
      // setUserId(user.id);
      dispatch({
        type: Types.ACCOUNT_SUCCESS,
        payload: user,
      });
    })
    .catch((error) => {
      dispatch({
        type: Types.ACCOUNT_FAIL,
        payload: error.error,
      });
    });
};

export const selectCountry = (code) => (dispatch, getState, { main }) => {
  const { giftcode, country } = getState().main;
  const isCountryNotSelected =
    (country && country.payload && country.payload.code) !==
    (code && code.code);

  if (isCountryNotSelected) {
    dispatch({
      type: Types.COUNTRY_START,
    });

    main
      .setCountry(giftcode, code.code)
      .then((payload) => {
        dispatch({
          type: Types.COUNTRY_SUCCESS,
          payload,
        });
      })
      .catch((error) => {
        dispatch({
          type: Types.COUNTRY_FAIL,
          payload: error.error,
        });
      });
  }
};

export const setUserAgree = () => (dispatch, getState, { main }) => {
  dispatch({
    type: Types.SET_USER_AGREE_START,
  });
  const { giftcode } = getState().main;
  return main
    .setUserAgree(giftcode)
    .then(() => {
      rememberTransferStart(giftcode);
      dispatch({ type: Types.SET_USER_AGREE_SUCCESS });
    })
    .catch((e) => {
      dispatch({
        type: Types.SET_USER_AGREE_FAIL,
        payload: e.error && e.error,
      });
    });
};

export function incStep() {
  return {
    type: Types.INC_STEP,
  };
}

export const changeAccount = () => (dispatch, getState, { main }) => {
  const { giftcode } = getState().main;
  return main
    .changeAccount(giftcode)
    .then((resp) => {
      removeUserId();
      dispatch({
        type: Types.CHANGE_ACCOUNT_SUCCESS,
      });
    })
    .catch((e) => {
      dispatch({
        type: Types.CHANGE_ACCOUNT_FAIL,
        payload: e.error && e.error,
      });
    });
};

export const getStatusRequest = () => (dispatch, getState, { main }) => {
  const { giftcode } = getState().main;
  const updatedAt = new Date().getTime();
  dispatch({
    type: Types.SET_FRIEND_DATA,
    payload: { friendRequest: { updatedAt, status: null }, bot: null },
  });

  const availableAt = Moment.getAvailableAt(updatedAt);

  function resendRequest() {
    main
      .getFriendRequest(giftcode)
      .then(({ error, friendRequest, bot, phase }) => {
        const isDone = friendRequest && friendRequest.status === 'DONE';
        const isError = error;
        const payload = { error };

        if (bot) {
          payload.bot = bot;
        }

        if (friendRequest) {
          payload.friendRequest = friendRequest;
        }

        if (phase) {
          payload.phase = phase;
        }
        dispatch({
          type: Types.SET_FRIEND_DATA,
          payload,
        });
        if (
          isDone ||
          (isError && isError.type === 1) ||
          !availableAt.isAfter(Moment.getCurrentMoment())
        ) {
          return;
        }
        setTimeout(() => resendRequest(), 15000);
      })
      .catch((error) => {
        dispatch({
          type: Types.SET_FRIEND_DATA,
          payload: error && error.error,
        });
      });
  }

  resendRequest();
};

export const stopGiftPolling = () => () => {
  if (giftTimer) clearTimeout(giftTimer);
  giftTimer = null;
  giftPollRun += 1;
};

// The only transfer-status poll: first GET at once, then every 15 s (60 s after 10 minutes),
// stops on a terminal state, on stopGiftPolling() and ignores late responses
export const getGiftData = () => (dispatch, getState, { main }) => {
  dispatch(stopGiftPolling());
  const run = giftPollRun;
  const pollingStartedAt = Date.now();
  dispatch({ type: Types.SET_GIFT_DATA, payload: { pollingStartedAt } });

  const isTerminal = () => {
    const { transferStatus, error } = getState().main;
    return isTransferTerminal(transferStatus, error);
  };
  if (isTerminal()) return;

  function poll() {
    const { giftcode } = getState().main;
    main
      .getGame(giftcode)
      .then(
        ({ transferStatus, transferStartedAt, bonusCode, error, phase }) => {
          if (run !== giftPollRun) return;
          const payload = { error };
          if (transferStatus) {
            payload.transferStatus = transferStatus;
            payload.bonusCode = bonusCode;
          }
          if (transferStartedAt) payload.transferStartedAt = transferStartedAt;
          if (phase) payload.phase = phase;
          dispatch({ type: Types.SET_FRIEND_DATA, payload });
        }
      )
      .catch(() => {
        // a failed GET is not shown — the next attempt follows
      })
      .then(() => {
        if (run !== giftPollRun || isTerminal()) return;
        const { transferStartedAt, giftcode: code } = getState().main;
        const anchor =
          resolveTransferStart(transferStartedAt, code) || pollingStartedAt;
        giftTimer = setTimeout(
          poll,
          isTransferSlow(anchor)
            ? GIFT_POLL_SLOW_INTERVAL_MS
            : GIFT_POLL_INTERVAL_MS
        );
      });
  }

  poll();
};
